import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";

export const ProfileContext = createContext({
  profile: null,
  profileLoading: false,
  beneficiaries: [],
  beneficiariesLoading: false,
  updateProfile: async () => {},
  addBeneficiary: async () => {},
  removeBeneficiary: async () => {},
  updatePermissions: async () => {},
  submitQuestionnaire: async () => {},
});

export const ProfileProvider = ({ children }) => {
  // ✅ Utiliser le profil d'AuthContext au lieu de faire un fetch
  const { profile, profileLoading } = useAuth();

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);

  const fetchBeneficiaries = useCallback(async () => {
    if (!profile?.id) { setBeneficiaries([]); return; }
    setBeneficiariesLoading(true);
    const { data, error } = await supabase
      .from("beneficiaries")
      .select("*")
      .eq("client_id", profile.id)
      .order("created_at", { ascending: true });
    if (error) {
      console.error("Erreur chargement bénéficiaires:", error);
    } else {
      setBeneficiaries(data || []);
    }
    setBeneficiariesLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  const updateProfile = async (updates) => {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id)
      .select()
      .single();

    if (error) {
      console.error("Erreur mise à jour profil:", error);
    }
    
    // Note: Pour que le profil se mette à jour, il faudrait
    // appeler fetchUserProfile() d'AuthContext, mais on n'a pas accès ici
    // Solution: forcer un refresh de la page ou utiliser un event
  };

  const addBeneficiary = async (beneficiaryData) => {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;

    const { error } = await supabase
      .from("beneficiaries")
      .insert([{ ...beneficiaryData, client_id: user.id }]);

    if (error) {
      console.error("Erreur ajout bénéficiaire:", error);
    } else {
      await fetchBeneficiaries();
    }
  };

  const removeBeneficiary = async (id) => {
    const { error } = await supabase.from("beneficiaries").delete().eq("id", id);
    if (error) {
      console.error("Erreur suppression bénéficiaire:", error);
    } else {
      await fetchBeneficiaries();
    }
  };

  const updatePermissions = async (beneficiaryId, permission) => {
    const { error } = await supabase
      .from("beneficiaries")
      .update({ permission })
      .eq("id", beneficiaryId);

    if (error) {
      console.error("Erreur mise à jour permissions:", error);
    }
  };

  const submitQuestionnaire = async (beneficiaryId, responses) => {
    const { error } = await supabase
      .from("questionnaires")
      .insert([{ beneficiary_id: beneficiaryId, responses }]);

    if (error) {
      console.error("Erreur soumission questionnaire:", error);
    }
  };

  const value = useMemo(
    () => ({
      profile,
      profileLoading,
      beneficiaries,
      beneficiariesLoading,
      updateProfile,
      addBeneficiary,
      removeBeneficiary,
      updatePermissions,
      submitQuestionnaire,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, profileLoading, beneficiaries, beneficiariesLoading]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};